import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import useMediaQuery from "@mui/material/useMediaQuery";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { isSellerAccountUser } from "../../../helpers/employees";
import { getMockUser, isImpersonating } from "../../../helpers/impersonation";
import {
  getCachedSellerOnboarding,
  loadSellerOnboarding,
  SELLER_ONBOARDING_REFETCH_MS,
  subscribeSellerOnboarding,
} from "./sellerOnboardingCache";
import { onboardingGateDecision } from "./onboardingGateDecision";

export { onboardingGateDecision };

const COPY = {
  continue: {
    severity: "warning",
    title: "Finish your application",
    body: "Finish your application so your orders can go to production.",
    cta: "Continue application",
  },
  needs_info: {
    severity: "warning",
    title: "We need a little more information",
    body: "Update your application so your orders can go to production.",
    cta: "Continue application",
  },
  grace: {
    severity: "info",
    title: "Complete your application",
    body: "Please complete your application so your orders keep going to production.",
    cta: "Continue application",
  },
  pending: {
    severity: "info",
    title: "Application under review",
    body: "Your application is being reviewed. Orders will go to production once it's approved.",
    cta: null,
  },
  closed: {
    severity: "error",
    title: "Your application wasn't approved",
    body: "Orders can't go to production on this account right now. Please contact the store if you have questions.",
    cta: null,
  },
};

const ICONS = {
  continue: <AssignmentOutlinedIcon fontSize="inherit" />,
  needs_info: <AssignmentOutlinedIcon fontSize="inherit" />,
  grace: <AssignmentOutlinedIcon fontSize="inherit" />,
  pending: <HourglassBottomIcon fontSize="inherit" />,
  closed: <InfoOutlinedIcon fontSize="inherit" />,
};

const readFlag = (key) => {
  if (!key) return true;
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return true; // storage blocked: never loop
  }
};

const writeFlag = (key) => {
  try {
    if (key) window.localStorage.setItem(key, "1");
  } catch {
    /* ignore */
  }
};

/**
 * Seller onboarding gate for the dashboard chrome.
 *  - locked access: sends the seller to /apply until they submit.
 *  - held access: sent to /apply once when they haven't started, then
 *  - held access (or locked + submitted): a global banner like
 *    SellerHoldBanner, with a "Continue application" link when relevant.
 * Admins, staff and admin impersonation never see it.
 */
const SellerOnboardingGate = () => {
  const user = useSelector((state) => state?.currentUser?.user);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width:600px)");

  const uid = user?.uid || null;
  const impersonating = isImpersonating(getMockUser());
  const isSeller = isSellerAccountUser(user);
  const active = Boolean(uid && isSeller && !impersonating);

  const [me, setMe] = useState(() => (uid ? getCachedSellerOnboarding(uid) : null));

  useEffect(() => {
    if (!active) {
      setMe(null);
      return undefined;
    }
    let alive = true;
    setMe(getCachedSellerOnboarding(uid));
    const unsubscribe = subscribeSellerOnboarding(() => {
      if (alive) setMe(getCachedSellerOnboarding(uid));
    });
    loadSellerOnboarding(uid).catch(() => {
      /* fail open: no gate when /me is unavailable */
    });
    const onFocus = () => {
      loadSellerOnboarding(uid, { maxAgeMs: SELLER_ONBOARDING_REFETCH_MS }).catch(
        () => {}
      );
    };
    window.addEventListener("focus", onFocus);
    return () => {
      alive = false;
      unsubscribe();
      window.removeEventListener("focus", onFocus);
    };
  }, [active, uid]);

  const introKey = uid ? `sellerOnboardingIntro:${uid}` : null;
  const introSeen = readFlag(introKey);
  const { redirect, banner, intro } = onboardingGateDecision({
    me,
    pathname: location.pathname,
    isSeller,
    impersonating,
    introSeen,
  });

  useEffect(() => {
    if (redirect && location.pathname !== redirect) {
      // First visit (held access): show the application once, then the
      // banner only, so sellers can still use the dashboard.
      if (intro) writeFlag(introKey);
      navigate(redirect, { replace: true });
    }
  }, [redirect, intro, introKey, location.pathname, navigate]);

  const visible = Boolean(banner);
  useEffect(() => {
    if (!visible) return undefined;
    document.body.classList.add("has-riverr-global-banner");
    return () => document.body.classList.remove("has-riverr-global-banner");
  }, [visible]);

  if (!banner) return null;

  const copy = COPY[banner.kind] || COPY.continue;
  const note =
    banner.kind === "needs_info" ? me?.application?.requestedInfo?.note : null;
  const goToApply = () => navigate("/apply");

  return (
    <Alert
      severity={copy.severity}
      icon={ICONS[banner.kind]}
      data-riverr-global-banner
      sx={{
        mt: "60px",
        borderRadius: 0,
        alignItems: "flex-start",
        "& .MuiAlert-message": { width: isMobile ? "100%" : undefined },
      }}
    >
      <AlertTitle sx={{ fontWeight: 700, fontSize: isMobile ? 13 : undefined }}>
        {copy.title}
      </AlertTitle>
      <div style={{ fontSize: isMobile ? 12.5 : undefined, lineHeight: 1.45 }}>
        {copy.body}
        {note ? (
          <div style={{ marginTop: 6, fontStyle: "italic" }}>“{note}”</div>
        ) : null}
      </div>
      {copy.cta && (
        <Button
          size="small"
          onClick={goToApply}
          sx={
            isMobile
              ? {
                  mt: 1,
                  width: "100%",
                  fontWeight: 700,
                  color: "inherit",
                  borderTop: "1px solid rgba(0,0,0,0.12)",
                  borderRadius: 0,
                  pt: 1,
                }
              : {
                  mt: 1,
                  fontWeight: 700,
                  color: "inherit",
                  border: "1px solid currentColor",
                  textTransform: "none",
                }
          }
        >
          {copy.cta}
        </Button>
      )}
    </Alert>
  );
};

export default SellerOnboardingGate;
